import { useCallback, useEffect, useRef, useState } from "react";
import type { CoachingNote, CoachingPlaylist } from "@/lib/types";

/**
 * Playlist session controller — turns a playlist's ordered notes
 * into a guided review session for the focused feedback player.
 *
 * Ports the legacy `startCoachingPlaylistSession` /
 * `openCoachingPlaylistItem` / `startPlaylistMonitor` machine
 * (`js/coaching/feedback-player.js:688-884`).
 *
 * Each playlist item is a coaching note. The session opens item 0
 * paused at its `timestamp_seconds` with the telestration visible
 * — the freeze IS the moment. Pressing Play reveals the post-roll
 * context; once the playhead passes `timestamp + post_roll_seconds`
 * (or the video ends) the 250ms advance monitor moves to the next
 * item. Manual Prev / Next / Restart jump items; Pause/Resume
 * gates both the `<video>` (via the modal) and the advance monitor.
 *
 * Pre-roll is intentionally skipped (legacy PR 1c decision): the
 * legacy flow auto-played pre-roll → freeze → post-roll, which
 * made the telestration feel fleeting. Opening paused at the
 * freeze is the better UX.
 *
 * This hook owns the SESSION STATE + the advance monitor. The
 * per-item video load + telestration replay is the
 * `FeedbackPlayerModal`'s job — it renders `session.currentItem`
 * through the same note-mode path as a standalone note, keyed on
 * `session.playKey` so each advance / restart remounts the player
 * cleanly.
 *
 * **M5 status:** the controller + the modal's `playlist` mode +
 * the rail UI ship now, fully unit-tested. The Coach Review
 * ENTRY POINT for playlist preview lands in M6 — playlists only
 * become browsable with M6's Coach Playlists list page (the M5
 * coach-review bundle carries a single note / clip, not a
 * playlist). Tracked, not dropped.
 */

const ADVANCE_MONITOR_INTERVAL_MS = 250;
/** Fallback post-roll when the playlist doesn't specify one —
 * matches the legacy default. */
const DEFAULT_POST_ROLL_SECONDS = 8;

export type PlaylistSession = {
  /** The playlist being played. */
  playlist: CoachingPlaylist;
  /** Ordered, visibility-filtered note items. */
  items: CoachingNote[];
  /** Current item index. */
  index: number;
  /** The current note item (convenience — `items[index]`). */
  currentItem: CoachingNote;
  /** Whether the session is paused (gates the `<video>` + the
   * advance monitor). */
  paused: boolean;
  /** Total item count (convenience for the rail UI). */
  total: number;
  /** Remount key for the modal's per-item player. Changes on
   * every item advance AND on a same-index restart, so the modal
   * can `key={session.playKey}` the note player and get a clean
   * remount either way. */
  playKey: string;
};

export type PlaylistSessionControls = {
  /** The live session, or `null` when no session is active (no
   * playlist, an empty playlist, or the session finished). */
  session: PlaylistSession | null;
  /** Jump to the next item; finishes the session past the last. */
  next: () => void;
  /** Jump to the previous item (clamped at 0). */
  prev: () => void;
  /** Re-open the current item from its freeze position. */
  restart: () => void;
  /** Toggle paused — gates the `<video>` (via the modal) + the
   * advance monitor. */
  togglePause: () => void;
  /** Tear the session down (the rail's "finish" / done action). */
  finish: () => void;
  /** Jump back to item 0 and re-arm the advance monitor. Drives the
   * "Review again" button on the completion state (PR 4). */
  reset: () => void;
  /** `true` after the last item finished — distinguishes the
   * post-completion state from an empty-bundle playlist. */
  finished: boolean;
  /** Whether the playlist carried any items at all. `false` ⇒ render
   * the empty-bundle copy instead of the post-completion copy. */
  hasItems: boolean;
};

/**
 * @param playlist The playlist to play, or `null` for no session.
 * @param videoRef The modal's `<video>` — the advance monitor
 *   watches its `currentTime` / `ended` to know when an item's
 *   post-roll window has elapsed.
 */
export function usePlaylistSession(
  playlist: CoachingPlaylist | null,
  videoRef: React.RefObject<HTMLVideoElement | null>,
): PlaylistSessionControls {
  // `index` past `items.length` means the session finished.
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // Bumped on every restart so a same-index restart still produces
  // a fresh `playKey` (and re-arms the advance monitor).
  const [restartNonce, setRestartNonce] = useState(0);

  const items = playlist?.items ?? [];
  const hasItems = !!playlist && items.length > 0;

  // Reset to item 0 / unpaused / nonce 0 whenever the playlist
  // identity changes. Keyed on `playlist?.id` (the stable
  // identity), NOT the object reference — a consumer that passes
  // an inline / re-derived playlist object would otherwise thrash
  // this effect on every render and never let the index advance.
  const playlistId = playlist?.id ?? null;
  useEffect(() => {
    setIndex(0);
    setPaused(false);
    setRestartNonce(0);
  }, [playlistId]);

  const finished = hasItems && index >= items.length;
  const safeIndex = hasItems
    ? Math.min(Math.max(0, index), items.length - 1)
    : 0;

  const next = useCallback(() => setIndex((i) => i + 1), []);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const restart = useCallback(() => setRestartNonce((n) => n + 1), []);
  const togglePause = useCallback(() => setPaused((p) => !p), []);
  // Finish jumps the index past the end — `finished` then resolves
  // the session to `null`.
  const finish = useCallback(() => setIndex(items.length), [items.length]);

  // The 250ms advance monitor — when the playhead passes the
  // current item's `timestamp + post_roll` (or the video ends),
  // and the session isn't paused, advance to the next item.
  //
  // The advance-window END is the only thing the monitor needs
  // from `items` / `playlist`; computing it here lets the effect
  // depend on a stable PRIMITIVE (`advanceWindowEnd`) instead of
  // the `items` array / `playlist` object — so a consumer passing
  // re-derived objects doesn't re-arm the interval every render.
  const currentItem = hasItems ? items[safeIndex] : undefined;
  const postRoll = playlist?.post_roll_seconds ?? DEFAULT_POST_ROLL_SECONDS;
  const advanceWindowEnd =
    Number(currentItem?.timestamp_seconds ?? 0) + postRoll;

  const monitorRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasItems || finished || paused) {
      if (monitorRef.current != null) {
        window.clearInterval(monitorRef.current);
        monitorRef.current = null;
      }
      return;
    }
    const tick = () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.currentTime >= advanceWindowEnd || video.ended) {
        setIndex((i) => i + 1);
      }
    };
    monitorRef.current = window.setInterval(tick, ADVANCE_MONITOR_INTERVAL_MS);
    return () => {
      if (monitorRef.current != null) {
        window.clearInterval(monitorRef.current);
        monitorRef.current = null;
      }
    };
  }, [
    hasItems,
    finished,
    paused,
    advanceWindowEnd,
    videoRef,
    // a restart re-arms the monitor against the re-seeked item
    restartNonce,
  ]);

  const session: PlaylistSession | null =
    hasItems && !finished
      ? {
          playlist: playlist!,
          items,
          index: safeIndex,
          currentItem: items[safeIndex]!,
          paused,
          total: items.length,
          playKey: `${safeIndex}-${restartNonce}`,
        }
      : null;

  // `finished` distinguishes "session played through to the end" from
  // "playlist had no items to begin with". The modal uses this to pick
  // the right completion state (PR 4 of the Feedback Player UX redesign:
  // "You're all caught up." + Review again / Back to feedback vs the
  // empty-bundle copy).
  const reset = useCallback(() => {
    setIndex(0);
    setPaused(false);
    setRestartNonce((n) => n + 1);
  }, []);

  return {
    session,
    next,
    prev,
    restart,
    togglePause,
    finish,
    reset,
    finished,
    hasItems,
  };
}
